// src/pages/Home.js

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { heroContent, designPrinciples, partnersContent, BEST_SELLER_HANDLE, LOW_STOCK, NEW_RELEASE_HANDLES } from '../data/scents';
import { getScents } from '../data/scents';
import { useCurrency } from '../context/CurrencyContext';
import { useCart, GIFT_BAG_HANDLE } from '../context/CartContext';
import ReviewsCarousel from '../components/ReviewsCarousel';
import ProductRatingBadge from '../components/ProductRatingBadge';
import { ChevronDown, ArrowRight } from 'lucide-react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';

const Home = () => {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [newReleases, setNewReleases] = useState([]);
    const { country, symbol } = useCurrency();
    const { addToCart, cart, loading: cartLoading } = useCart();
    const [buyNowPending, setBuyNowPending] = useState(false);
    const cartBeforeRef = useRef(null);

    useEffect(() => {
        getScents(country)
            .then(products => {
                setFeaturedProducts(products.slice(0, 3));
                // In NEW_RELEASE_HANDLES order; a scent not yet published in
                // Shopify isn't returned, so the section stays hidden until then.
                setNewReleases(NEW_RELEASE_HANDLES
                    .map(handle => products.find(p => p.handle === handle))
                    .filter(Boolean));
            })
            .catch(err => console.error('Failed to load featured products:', err));
    }, [country]);

    // Only redirect to checkout once addToCart's mutation has actually landed —
    // detected by the cart object changing identity, since CartContext only
    // calls setCart on a successful add, never on failure. A failed add leaves
    // cart unchanged, so redirecting on its (stale) checkoutUrl would send the
    // shopper to checkout without the item they just tried to buy.
    useEffect(() => {
        if (!buyNowPending || cartLoading) return;
        setBuyNowPending(false);
        if (cart !== cartBeforeRef.current && cart?.checkoutUrl) {
            window.location.href = cart.checkoutUrl;
        }
    }, [buyNowPending, cartLoading, cart]);

    const handleBuyNow = (product) => {
        cartBeforeRef.current = cart;
        setBuyNowPending(true);
        addToCart(product.variantId, 1);
    };

    return (
        <div className="bg-background min-h-screen transition-colors duration-300">

            {/* ── Hero — single static image ────────────────────────────────── */}
            <section style={{ position: 'relative', height: '100vh', minHeight: '600px', overflow: 'hidden' }}>

                <img
                    src="/images/marina-market/unreleased_teaser.jpg"  // temporary for the new-scents launch; normally /images/hero-banner.jpg
                    alt=""
                    aria-hidden="true"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center center',  // shows bottle body, not just the cap
                    }}
                />

                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.55) 100%)', zIndex: 1 }} />

                <div style={{
                    position: 'absolute', inset: 0, zIndex: 2,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    padding: '0 24px', textAlign: 'center',
                }}>
                    <h1 style={{ fontFamily: "'Tenor Sans', sans-serif", color: '#fff', fontSize: 'clamp(2rem, 5.5vw, 4.5rem)', fontWeight: 400, lineHeight: 1.2, marginBottom: '24px', maxWidth: '800px' }}>
                        {heroContent.tagline}
                    </h1>
                    <p style={{
                        fontFamily: "'Tenor Sans', sans-serif",
                        color: 'rgba(255,255,255,0.65)',
                        fontSize: 'clamp(11px, 3.5vw, 18px)',
                        letterSpacing: 'clamp(0.06em, 1.5vw, 0.20em)',
                        textTransform: 'uppercase',
                        marginBottom: '40px',
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                    }}>
                        Signature Concentrated Perfume Oils
                    </p>
                    <Link to="/scents" style={{ fontFamily: "'Tenor Sans', sans-serif", display: 'inline-flex', alignItems: 'center', gap: '10px', backgroundColor: '#CEAC8F', color: '#1a1a1a', padding: '14px 36px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', letterSpacing: '0.08em' }}>
                        Explore Atlas Collection <ArrowRight size={16} />
                    </Link>
                </div>

                <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce" style={{ zIndex: 2 }}>
                    <ChevronDown size={22} style={{ color: 'rgba(255,255,255,0.4)' }} />
                </div>
            </section>

            {/* ── New Releases ──────────────────────────────────────────────── */}
            {newReleases.length > 0 && (
                <section className="py-20 px-6 bg-background transition-colors duration-300">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p className="text-xs tracking-[0.3em] uppercase text-[#CEAC8F] mb-3">Just Landed</p>
                            <h2 className="text-4xl md:text-5xl font-light text-foreground tracking-wide">New Releases</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                            {newReleases.map((product) => (
                                <div key={product.id} className="group relative bg-card rounded-2xl overflow-hidden transition-all duration-500">
                                    <span className="absolute top-3 left-3 z-10 bg-[#4E2326] text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                        New
                                    </span>
                                    <Link to={`/product/${product.handle}`}>
                                        <div className="aspect-[3/4] overflow-hidden">
                                            <img src={product.image} alt={product.imageAlt} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                                        </div>
                                        <div className="p-6 pb-3">
                                            <h3 className="text-foreground text-xl font-light mb-1">{product.name}</h3>
                                            <ProductRatingBadge productId={product.id} productTitle={product.name} />
                                            <p className="text-muted-foreground/70 text-xs tracking-widest mb-2 uppercase">12ml</p>
                                            <p className="text-[#CEAC8F] text-2xl font-light mb-4">{symbol}{product.price.toFixed(2)}</p>
                                        </div>
                                    </Link>
                                    {product.availableForSale && (
                                        <div className="px-6 pb-6">
                                            <button onClick={() => handleBuyNow(product)} disabled={cartLoading} className="w-full py-3 rounded-lg font-medium bg-[#CEAC8F] text-black hover:bg-[#d4b896] transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed">
                                                Buy Now
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Atlas Collection (moved above Featured Products for visibility as the new collection) ── */}
            <section className="py-24 px-6 bg-muted/30 transition-colors duration-300">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <p className="text-xs tracking-[0.3em] uppercase text-[#CEAC8F] mb-3">New Collection</p>
                        <h2 className="text-4xl md:text-5xl font-light text-foreground mb-4">Atlas</h2>
                        <p className="text-muted-foreground max-w-md mx-auto">Wear it daily. Leave it behind.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                        <div className="rounded-2xl overflow-hidden">
                            <img src="/images/MaisonImage8.jpeg" alt="Atlas – Maison D'Oud on antique atlas" loading="lazy" className="w-full h-auto block" />
                        </div>
                        <div className="rounded-2xl overflow-hidden md:-mt-8">
                            <img src="/images/MaisonImage18.jpeg" alt="Atlas – collection detail" loading="lazy" className="w-full h-auto block" />
                        </div>
                        <div className="rounded-2xl overflow-hidden">
                            <img src="/images/MaisonImage19.jpeg" alt="Atlas – on dark leather and maps" loading="lazy" className="w-full h-auto block" />
                        </div>
                    </div>
                    <div className="text-center">
                        <Link to="/scents" className="inline-flex items-center gap-2 text-[#CEAC8F] hover:gap-4 transition-all">
                            Shop the Atlas Collection <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Featured Products ─────────────────────────────────────────── */}
            <section className="py-24 px-6 bg-muted/30 transition-colors duration-300">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-5xl md:text-6xl font-light text-foreground mb-4 tracking-wide">Signature Scents</h2>
                        <p className="text-muted-foreground">Signature perfume oils that define presence.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                        {featuredProducts.map((product) => (
                            <div key={product.id} className="group relative bg-card rounded-2xl overflow-hidden transition-all duration-500">
                                {(NEW_RELEASE_HANDLES.includes(product.handle) || product.handle === BEST_SELLER_HANDLE || LOW_STOCK[product.handle]) && (
                                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
                                        {NEW_RELEASE_HANDLES.includes(product.handle) && (
                                            <span className="bg-[#4E2326] text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                New
                                            </span>
                                        )}
                                        {product.handle === BEST_SELLER_HANDLE && (
                                            <span className="bg-[#CEAC8F] text-black text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                Best Seller
                                            </span>
                                        )}
                                        {LOW_STOCK[product.handle] && (
                                            <span className="bg-red-600 text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                Low Stock
                                            </span>
                                        )}
                                    </div>
                                )}
                                <Link to={`/product/${product.handle}`}>
                                    <div className="aspect-[3/4] overflow-hidden">
                                        <img src={product.image} alt={product.imageAlt} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                                    </div>
                                    <div className="p-6 pb-3">
                                        {product.tags?.length > 0 && (
                                            <p className="text-[#CEAC8F]/70 text-xs tracking-widest mb-2 uppercase">{product.tags[0]}</p>
                                        )}
                                        <h3 className="text-foreground text-xl font-light mb-1">{product.name}</h3>
                                        <ProductRatingBadge productId={product.id} productTitle={product.name} />
                                        {product.handle !== GIFT_BAG_HANDLE && (
                                            <p className="text-muted-foreground/70 text-xs tracking-widest mb-2 uppercase">12ml</p>
                                        )}
                                        <p className="text-[#CEAC8F] text-2xl font-light mb-4">{symbol}{product.price.toFixed(2)}</p>
                                    </div>
                                </Link>
                                {product.availableForSale && (
                                    <div className="px-6 pb-6">
                                        <button onClick={() => handleBuyNow(product)} disabled={cartLoading} className="w-full py-3 rounded-lg font-medium border border-[#CEAC8F] text-[#CEAC8F] hover:bg-[#CEAC8F] hover:text-black transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed">
                                            Buy Now
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="text-center">
                        <Link to="/scents" className="inline-flex items-center gap-2 text-[#CEAC8F] hover:gap-4 transition-all">
                            View All Scents <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Lifestyle ─────────────────────────────────────────────────── */}
            {/*
            <section className="py-24 px-6 bg-background transition-colors duration-300">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <p className="text-xs tracking-[0.3em] uppercase text-[#CEAC8F] mb-3">The World of MAISON D'OUD</p>
                        <h2 className="text-4xl md:text-5xl font-light text-foreground">Rooted in Tradition</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="rounded-2xl overflow-hidden">
                            <img src="/images/MaisonImage7.jpeg" alt="Atlas collection on antique maps" loading="lazy" className="w-full h-auto block" />
                        </div>
                        <div className="rounded-2xl overflow-hidden">
                            <img src="/images/MaisonImage5.jpeg" alt="Maison D'Oud gift box with rose petals" loading="lazy" className="w-full h-auto block" />
                        </div>
                    </div>
                </div>
            </section>
            */}

            {/* ── Scent Quiz prompt (the quiz itself lives at /quiz) ────────── */}
            <section className="py-16 px-6 bg-background transition-colors duration-300">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="text-[#CEAC8F] text-xs tracking-widest uppercase mb-3">Find Your Scent</p>
                    <h2 className="text-3xl md:text-4xl font-light text-foreground mb-4 tracking-wide">
                        Not sure where to start?
                    </h2>
                    <p className="text-muted-foreground text-lg mb-8">
                        Six questions. Zero judgement. One very good match.
                    </p>
                    <Link to="/quiz" style={{ fontFamily: "'Tenor Sans', sans-serif", display: 'inline-flex', alignItems: 'center', gap: '10px', backgroundColor: '#CEAC8F', color: '#1a1a1a', padding: '14px 36px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', letterSpacing: '0.08em' }}>
                        Take the Scent Quiz <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

            {/* ── Our Philosophy ────────────────────────────────────────────── */}
            <section className="py-24 px-6 bg-muted/30 transition-colors duration-300">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                    <div>
                        <h2 className="text-4xl md:text-5xl font-light text-foreground mb-12">Our Philosophy</h2>
                        <Accordion type="single" collapsible className="space-y-4">
                            {designPrinciples.map((principle, index) => (
                                <AccordionItem key={index} value={`item-${index}`} className="border border-border rounded-lg px-6 data-[state=open]:border-[#CEAC8F]/30 transition-colors">
                                    <AccordionTrigger className="text-foreground hover:text-[#CEAC8F] text-left py-6 hover:no-underline focus:outline-none focus-visible:outline-none">
                                        {principle.title}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-muted-foreground pb-6 leading-relaxed">
                                        {principle.description}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                    <div className="rounded-2xl overflow-hidden" style={{ height: '520px' }}>
                        <img
                            src="/images/MaisonImage6.jpeg"
                            alt="Maison D'Oud – hand holding perfume oil"
                            className="w-full h-full object-cover block"
                            style={{ objectPosition: 'center 40%' }}
                        />
                    </div>
                </div>
            </section>

            {/* ── Gifting ───────────────────────────────────────────────────── */}
            <section className="py-24 px-6 bg-background transition-colors duration-300">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <p className="text-xs tracking-[0.3em] uppercase text-[#CEAC8F] mb-4">The Perfect Gift</p>
                        <h2 className="text-4xl md:text-5xl font-light text-foreground mb-6">Unforgettable,<br />by Design</h2>
                        <p className="text-muted-foreground text-lg leading-relaxed mb-10 max-w-md">
                            Every bottle arrives in our signature gift box, a keepsake as considered as the scent within.
                        </p>
                        <Link to="/scents" className="inline-flex items-center gap-2 text-[#CEAC8F] hover:gap-4 transition-all">
                            Shop All Scents <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 gap-4 items-start">
                        <div className="rounded-2xl overflow-hidden">
                            <img src="/images/MaisonImage2.jpeg" alt="Maison D'Oud gift box" loading="lazy" className="w-full h-auto block" />
                        </div>
                        <div className="rounded-2xl overflow-hidden mt-8">
                            <img src="/images/MaisonImage3.jpeg" alt="Maison D'Oud gift box angled" loading="lazy" className="w-full h-auto block" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Reviews ───────────────────────────────────────────────────── */}
            <ReviewsCarousel />

            {/* ── Our Standard ──────────────────────────────────────────────── */}
            <section className="py-24 px-6 bg-muted/30 transition-colors duration-300">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl md:text-5xl font-light text-foreground mb-8">{partnersContent.heading}</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">{partnersContent.description}</p>
                </div>
            </section>

        </div>
    );
};

export default Home;